import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import AppLogo from "@/components/AppLogo";

export default function RegisterPage() {
  const [step, setStep] = useState("check"); // check, register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailAuthorized, setEmailAuthorized] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const checkEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/check-email", { email });
      
      if (res.data.already_registered) {
        toast.error("This email is already registered. Please sign in.");
        navigate("/login");
        return;
      }
      
      if (res.data.authorized) {
        setEmailAuthorized(true);
        setStep("register");
        toast.success("Email verified! Please complete registration.");
      } else {
        setEmailAuthorized(false);
      }
    } catch (error) {
      toast.error("Error checking email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!name || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card animate-fadeIn">
        <div className="auth-logo">
          <AppLogo size="large" showText={true} className="flex-col" />
        </div>

        <Card className="border-0 shadow-none">
          <CardHeader className="space-y-1 px-0">
            <CardTitle className="text-2xl font-heading">
              {step === "check" ? "Create account" : "Complete registration"}
            </CardTitle>
            <CardDescription>
              {step === "check" 
                ? "Enter your email to check access" 
                : "Set up your account details"}
            </CardDescription>
          </CardHeader>

          {step === "check" ? (
            <form onSubmit={checkEmail}>
              <CardContent className="space-y-4 px-0">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailAuthorized(null);
                    }}
                    className="h-12"
                    data-testid="register-email-input"
                    autoComplete="email"
                  />
                </div>

                {emailAuthorized === false && (
                  <Alert variant="destructive" className="animate-fadeIn">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      This email is not authorized to register. Please contact the administrator for access.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>

              <CardFooter className="flex-col gap-4 px-0">
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full font-semibold"
                  disabled={loading}
                  data-testid="check-email-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    "Continue"
                  )}
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-primary hover:underline font-medium"
                    data-testid="login-link"
                  >
                    Sign in
                  </Link>
                </p>
              </CardFooter>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <CardContent className="space-y-4 px-0">
                <Alert className="bg-green-50 border-green-200 text-green-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    Email verified: <strong>{email}</strong>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12"
                    data-testid="register-name-input"
                    autoComplete="name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12"
                    data-testid="register-password-input"
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12"
                    data-testid="register-confirm-password-input"
                    autoComplete="new-password"
                  />
                </div>
              </CardContent>

              <CardFooter className="flex-col gap-4 px-0">
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full font-semibold"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep("check")}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Use a different email
                </button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
